import { ArrowDownLeft, ArrowUpRight, ReceiptText } from 'lucide-react'
import styles from './Financial.module.css'
import BlankState from '../BlankState/BlankState'
import { fmtEUR, timeAgo } from '../../../utils/moneyFormat'

// Status → pill tone. Anything unmapped (cancelled, refunded, unknown) stays
// neutral rather than borrowing a colour it hasn't earned.
const PILL_TONE = {
  completed: 'dshPillPos',
  pending: 'dshPillWarn',
  failed: 'dshPillNeg',
}

// Live finance snapshot (data from /dashboard/overview → finance):
// collected client money, open outstanding across bookings (net-pool math)
// and this month's client refunds, plus the most recent payments.
const FinanceHealth = ({ finance, loading }) => {
  const figures = [
    { label: 'Collected', value: finance?.totalCollected, tone: 'figFg' },
    { label: 'Outstanding', value: finance?.totalOutstanding, tone: 'figWarn' },
    { label: 'Refunded', value: finance?.refundsInPeriod, tone: 'figMuted' },
  ]

  const transactions = finance?.transactions || []

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div className={styles.headText}>
          <h3 className="dshTitle">Payments</h3>
          <p className="dshSub">Collected, outstanding &amp; refunded</p>
        </div>
      </div>

      <div className={styles.figures}>
        {figures.map((f) => (
          <div key={f.label} className={styles.fig}>
            <span className="dshEyebrow">{f.label}</span>
            {loading ? (
              <span className={`dshSkel ${styles.skelFig}`} />
            ) : (
              <span className={`dshNum ${styles.figValue} ${styles[f.tone]}`}>
                {fmtEUR(f.value)}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="dshBody">
        <span className={`dshEyebrow ${styles.sectionLabel}`}>Recent transactions</span>

        <div className={styles.list}>
          {loading &&
            [0, 1, 2].map((i) => (
              <div key={i} className={`dshRow ${styles.row}`}>
                <span className={`dshSkel ${styles.skelBadge}`} />
                <div className={styles.txMain}>
                  <span className={`dshSkel ${styles.skelName}`} />
                  <span className={`dshSkel ${styles.skelMeta}`} />
                </div>
                <div className={styles.txRight}>
                  <span className={`dshSkel ${styles.skelAmount}`} />
                  <span className={`dshSkel ${styles.skelPill}`} />
                </div>
              </div>
            ))}

          {!loading && transactions.length === 0 && (
            <BlankState
              icon={ReceiptText}
              title="No transactions yet"
              text="Client payments and supplier payouts appear here the moment they are registered."
            />
          )}

          {!loading &&
            transactions.map((t) => {
              const incoming = t.direction === 'in'
              return (
                <div key={t.id} className={`dshRow ${styles.row}`}>
                  <span
                    className={`dshAvatar ${styles.badge} ${incoming ? 'dshTone1' : 'dshTone4'}`}
                    aria-hidden="true"
                  >
                    {incoming ? (
                      <ArrowDownLeft size={14} strokeWidth={2.25} />
                    ) : (
                      <ArrowUpRight size={14} strokeWidth={2.25} />
                    )}
                  </span>

                  <div className={styles.txMain}>
                    <span className={`dshTrunc ${styles.txName}`}>{t.name}</span>
                    <span className={`dshTrunc ${styles.txMeta}`}>
                      {t.type} · {timeAgo(t.date)}
                    </span>
                  </div>

                  <div className={styles.txRight}>
                    <span
                      className={`dshNum ${styles.amount} ${
                        incoming ? styles.amountIn : styles.amountOut
                      }`}
                    >
                      {incoming ? '+' : '−'} {fmtEUR(t.amount)}
                    </span>
                    <span
                      className={`dshPill ${PILL_TONE[t.status] || 'dshPillMute'} ${styles.statusPill}`}
                    >
                      {t.status}
                    </span>
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default FinanceHealth
